import React, { useContext, useEffect, useState } from 'react';
import PhoneScreen from './components/phone-screen';
import QuestionnaireScreen from './components/questionnaire-screen';
import SuccessScreen from './components/success-screen';
import UnavailableScreen, {
  UNAVAILABLE_REASONS
} from './components/unavailable-screen';
import CompanyInfoScreen from '../../../common/registration-flow-wrapper/components/company-info-screen';
import CookieManagementScreen from '../../../common/registration-flow-wrapper/components/cookie-management-screen';
import smsFeedbackService, {
  SMS_FEEDBACK_ERROR_CODES,
  getErrorCode
} from '../../../services/smsFeedbackService';
import { DataLayerEventContext } from '../../../contexts/dataLayerEventContext';

// SMS-опитувальник для 26UA-PRL-Sign (Parliament Signature).
//
// Механіка спільна для всіх брендів, але дизайн — ні, тому цей флоу і всі
// його екрани лежать окремо під цей SKU. Для наступного бренду робимо сусідню
// папку src/components/sms-feedback/<sku> зі своїми екранами, а не правимо цю:
// спільними лишаються тільки запити (services/smsFeedbackService).
//
// Внутрішні екрани флоу. Нумерація з 101, щоб не перетинатися з екранами
// сторінки, коли крок пишеться в History API.
const SCREENS = {
  PHONE: 101,
  QUESTIONNAIRE: 102,
  SUCCESS: 103,
  UNAVAILABLE: 104,
  TERMS: 105,
  COOKIES: 107
};

const COMMON_ERROR_TEXT = 'Щось пішло не так. Спробуй ще раз';

// Кампанія, до якої прив'язаний відгук: "sku_code" з посилання
// https://yourjourney.com.ua/sms-feedback?campaign_name={sku_code}&channel=sms
// Фолбек на utm_campaign лишений на випадок, якщо форму відкриють зі старого
// посилання бренду.
const getCampaignName = () => {
  const params = new URLSearchParams(window.location.search);
  const campaignName =
    params.get('campaign_name') || params.get('utm_campaign');

  if (campaignName) return campaignName;

  const isQrCode = sessionStorage.getItem('isQrCode') || '';

  return isQrCode.includes('utm_campaign=')
    ? isQrCode.split('utm_campaign=')[1].split('&')[0]
    : '';
};

const PrlSignSmsFeedbackFlow = ({
  innerScreenNumber = null,
  setInnerScreenNumber = null
}) => {
  const { sendEvent } = useContext(DataLayerEventContext);

  // Коли батьківська сторінка передає setInnerScreenNumber — кроки пишуться
  // в історію браузера, інакше працює звичайний внутрішній стан.
  const isHistoryEnabled = !!setInnerScreenNumber;
  const [internalScreen, setInternalScreen] = useState(SCREENS.PHONE);

  const screen = isHistoryEnabled
    ? innerScreenNumber || SCREENS.PHONE
    : internalScreen;

  const goToScreen = (target) => {
    if (isHistoryEnabled) setInnerScreenNumber(target);
    else setInternalScreen(target);
  };

  const [brands, setBrands] = useState([]);
  const [unavailableReason, setUnavailableReason] = useState(
    UNAVAILABLE_REASONS.NOT_IDENTIFIED
  );
  const [feedbackSubmitError, setFeedbackSubmitError] = useState('');

  useEffect(() => {
    sendEvent('review');
  }, []);

  // Бренди для двох селектів у кінці форми. Помилка тут не блокує відгук —
  // просто лишаються порожні списки.
  useEffect(() => {
    let isActive = true;

    smsFeedbackService
      .getBrands()
      .then((list) => {
        if (isActive) setBrands(list);
      })
      .catch(() => {});

    return () => {
      isActive = false;
    };
  }, []);

  const showUnavailable = (reason) => {
    setUnavailableReason(reason);
    goToScreen(SCREENS.UNAVAILABLE);
  };

  // Варіант екрана "недоступне": окремий лише для вичерпаного місячного
  // ліміту, у всіх інших випадках — "профіль не ідентифіковано".
  const getUnavailableReason = (error) =>
    getErrorCode(error) === SMS_FEEDBACK_ERROR_CODES.MONTHLY_LIMIT_EXCEEDED
      ? UNAVAILABLE_REASONS.MONTHLY_LIMIT
      : UNAVAILABLE_REASONS.NOT_IDENTIFIED;

  // Будь-яка помилка бекенда на кроці з телефоном закриває доступ до
  // опитувальника — показуємо feedback-36, а не лишаємо користувача на формі.
  const handlePhoneSubmit = async (phone) => {
    try {
      await smsFeedbackService.authByPhone(phone);
    } catch (e) {
      showUnavailable(getUnavailableReason(e));
      return;
    }

    // Перевіряємо ліміт до показу форми, щоб не давати заповнити її даремно.
    try {
      const availability =
        await smsFeedbackService.getAvailability(getCampaignName());

      if (availability && availability.available === false) {
        showUnavailable(UNAVAILABLE_REASONS.MONTHLY_LIMIT);
        return;
      }
    } catch (e) {
      showUnavailable(getUnavailableReason(e));
      return;
    }

    goToScreen(SCREENS.QUESTIONNAIRE);
  };

  const handleFeedbackSubmit = async (values) => {
    setFeedbackSubmitError('');

    try {
      await smsFeedbackService.createSmsFeedback({
        campaignName: getCampaignName(),
        ...values
      });

      goToScreen(SCREENS.SUCCESS);
    } catch (e) {
      if (getErrorCode(e) === SMS_FEEDBACK_ERROR_CODES.MONTHLY_LIMIT_EXCEEDED) {
        showUnavailable(UNAVAILABLE_REASONS.MONTHLY_LIMIT);
      } else {
        setFeedbackSubmitError(COMMON_ERROR_TEXT);
      }
    }
  };

  const footerHandlers = {
    onTerms: () => goToScreen(SCREENS.TERMS),
    onCookies: () => goToScreen(SCREENS.COOKIES)
  };

  const handleBackFromInfoScreen = () => goToScreen(SCREENS.PHONE);

  switch (screen) {
    case SCREENS.QUESTIONNAIRE:
      return (
        <QuestionnaireScreen
          brands={brands}
          submitError={feedbackSubmitError}
          onSubmit={handleFeedbackSubmit}
        />
      );
    case SCREENS.SUCCESS:
      return <SuccessScreen />;
    case SCREENS.UNAVAILABLE:
      return (
        <UnavailableScreen reason={unavailableReason} {...footerHandlers} />
      );
    case SCREENS.TERMS:
      return (
        <CompanyInfoScreen
          setScreenNumber={handleBackFromInfoScreen}
          handleBackButtonClick={handleBackFromInfoScreen}
        />
      );
    case SCREENS.COOKIES:
      return (
        <CookieManagementScreen setScreenNumber={handleBackFromInfoScreen} />
      );
    default:
      return <PhoneScreen onSubmit={handlePhoneSubmit} {...footerHandlers} />;
  }
};

export default PrlSignSmsFeedbackFlow;
